import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axiosInstant from '../api';
import { getBrandOrder } from '../api/endpoints';

/**
 * A project that belongs to the same expert-selection process (brand_order_pros)
 * as the one being viewed. The current project is always included in
 * siblingProjects when it resolves to a process.
 */
export interface ProcessSibling {
    id: number;
    title: string;
    status?: string;
    brand_order_pros_id?: number | null;
}

export interface ProcessSiblingsResult {
    /** The current project's brand_order_pros_id, or null if it has none / not loaded. */
    processId: number | null;
    /** Every project on the process (INCLUDING the current one). */
    siblingProjects: ProcessSibling[];
    /** Ids of every project on the process (including the current one). */
    siblingProjectIds: number[];
    /** Ids of the OTHER projects on the process (excludes the current one). */
    otherProjectIds: number[];
    /**
     * True when the process spans more than one project — i.e. casting /
     * ranking / confirmation actions here also affect sibling projects. On
     * historical (1 process : 1 project) data this is always false.
     */
    isShared: boolean;
    isLoading: boolean;
    error: unknown;
    /** Re-run the fetch (e.g. after the order's projects change). */
    refetch: () => void;
}

type State = Omit<ProcessSiblingsResult, 'refetch'>;

const EMPTY: State = {
    processId: null,
    siblingProjects: [],
    siblingProjectIds: [],
    otherProjectIds: [],
    isShared: false,
    isLoading: false,
    error: null,
};

/**
 * Resolves the set of projects that share a project's expert-selection process.
 *
 * The backend reparent made experts live on the process (brand_order_pros), not
 * the project, so a single order can have one process spanning several
 * same-product projects. This hook fetches the order once (getBrandOrder returns
 * every project's brand_order_pros_id — the authoritative source, same shape the
 * order-form modal consumes) and groups the projects by the current project's
 * process so the casting UI can warn/label when an action is shared.
 *
 * Skips the fetch when orderId/projectId are missing or skip=true (e.g. a
 * public-token page that can't call the authenticated order endpoint). In every
 * skipped/failed case isShared is false, so callers degrade to the historical
 * single-project behaviour.
 */
export function useProcessSiblings(
    orderId: number | undefined,
    projectId: number | undefined,
    {
        skip = false,
        siblingCount,
    }: { skip?: boolean; siblingCount?: number } = {},
): ProcessSiblingsResult {
    const [state, setState] = useState<State>(EMPTY);
    // Monotonic request token so a later fetch supersedes an older in-flight one.
    const reqIdRef = useRef(0);

    const refetch = useCallback(() => {
        // Skip the fetch when the caller already knows the process isn't shared
        // (siblingCount <= 1) — avoids a getBrandOrder round-trip on the common
        // 1:1 case that dominates historical data.
        if (
            skip ||
            !orderId ||
            !projectId ||
            (siblingCount != null && siblingCount <= 1)
        ) {
            reqIdRef.current += 1;
            setState(EMPTY);
            return;
        }
        reqIdRef.current += 1;
        const myReq = reqIdRef.current;
        setState((s) => ({ ...s, isLoading: true, error: null }));
        axiosInstant
            .get(getBrandOrder(orderId))
            .then((res) => {
                if (myReq !== reqIdRef.current) return;
                const projects: ProcessSibling[] = res.data?.projects || [];
                const current = projects.find(
                    (p) => Number(p.id) === Number(projectId),
                );
                const pid = current?.brand_order_pros_id ?? null;
                // No process id → treat the current project as its own singleton.
                let siblings: ProcessSibling[];
                if (pid == null) {
                    siblings = current ? [current] : [];
                } else {
                    siblings = projects.filter(
                        (p) => p.brand_order_pros_id === pid,
                    );
                }
                const siblingProjectIds = siblings.map((p) => p.id);
                setState({
                    processId: pid,
                    siblingProjects: siblings,
                    siblingProjectIds,
                    otherProjectIds: siblingProjectIds.filter(
                        (id) => Number(id) !== Number(projectId),
                    ),
                    isShared: siblings.length > 1,
                    isLoading: false,
                    error: null,
                });
            })
            .catch((err) => {
                if (myReq !== reqIdRef.current) return;
                // Non-fatal: without this the UI just falls back to single-project
                // behaviour (no shared-process banner). Log and leave empty.
                // eslint-disable-next-line no-console
                console.error('useProcessSiblings failed:', err);
                setState({ ...EMPTY, error: err });
            });
    }, [orderId, projectId, skip, siblingCount]);

    useEffect(() => {
        refetch();
        return () => {
            reqIdRef.current += 1;
        };
    }, [refetch]);

    return useMemo(() => ({ ...state, refetch }), [state, refetch]);
}
